/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BaseStepCompProps } from '../types';
import { CompanyInfoForm } from '../partials/CompanyInfoForm';
import G17Client from '../../../services/G17Client';
import { companyTrackerRouteMatcher } from '../../../routes/appRootMatcher';
import LoadingBar from '../partials/LoadingBar';
import { SettingStorage } from '../../../services/SettingStorage';
import { BasicAlert } from '../../alert/BasicAlert';
import { useAppSelector } from '../../../reducers';
import { isStaff, isUserActivated } from '../../../selectors/user';

interface CompanyInfoProps extends Pick<BaseStepCompProps,
  'updateForm' |
  'formData' |
  'handleNext' |
  'handlePrevious' |
  'appConfig'
> {
}

export const CompanyInfo = (props: CompanyInfoProps) => {
  const { formData, handleNext, appConfig } = props;

  const onboardingPath = appConfig.onboardingPath;
  const isActivated = useAppSelector(isUserActivated);
  const isUserStaff = useAppSelector(isStaff);
  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isActivated && !isUserStaff) {
      // Skip this step if already activated,
      // except if Staff to allow creating companies
      // without needing to create a new user
      handleNext();
    }
  }, [isActivated, isUserStaff, handleNext]);

  const handleCreateCompany = useCallback(async () => {
    setErrorMessage('')
    setSubmitting(true)
    try {
      const { survey } = await G17Client.createCompanyWithSurvey(formData, onboardingPath);

      // Refresh user to get new permissions

      // Pre-select organization for new user
      SettingStorage.setItem(companyTrackerRouteMatcher.storageKey, survey.initiativeId);
      setSubmitting(false)
      handleNext();

      // Reload to get latest initiative tree, root orgs list, current user with permissions
      // I don't like this... maybe should future refresh individual states
      window.location.reload();
    } catch (e) {
      setSubmitting(false)
      setErrorMessage(e.message)
    }
  }, [formData, onboardingPath, setSubmitting, handleNext, setErrorMessage]);

  if (isSubmitting) {
    return <LoadingBar />;
  }

  return <>
    <BasicAlert type='danger'>{errorMessage}</BasicAlert>
    <div className='d-lg-flex justify-content-between onboarding-form'>
      <aside className='title-container'>
        <div className='h4 text-strong'>Company details</div>
      </aside>
      <CompanyInfoForm {...props} handleCreateCompany={handleCreateCompany} />
    </div>
  </>
}
